import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getInstructorSession, getPortalInstructors, getInstructorSchedule,
  pairDevice, setDeviceToken, clearDeviceToken,
} from '../../services/api';
import ClassRegister from './ClassRegister';
import PairingCodeInput from './PairingCodeInput';

// Everything that turns this route into a home-screen app on the academy's
// iPad — full screen, its own icon, no Safari chrome around the register.
//
// Injected from here rather than written into index.html because that one file
// serves the admin portal too, and adding *that* to a home screen must not
// produce something calling itself the Register. Removed again on unmount, so
// the tags exist only while this route is on screen.
const WEB_APP_TAGS = [
  ['meta', { name: 'apple-mobile-web-app-capable', content: 'yes' }],
  ['meta', { name: 'mobile-web-app-capable', content: 'yes' }],
  ['meta', { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' }],
  ['meta', { name: 'apple-mobile-web-app-title', content: 'Register' }],
  ['meta', { name: 'theme-color', content: '#221420' }],
  ['link', { rel: 'apple-touch-icon', href: '/brand/stable-mark-180.png' }],
  ['link', { rel: 'manifest', href: '/instructor.webmanifest' }],
];

function useHomeScreenApp() {
  useEffect(() => {
    const added = WEB_APP_TAGS.map(([tag, attrs]) => {
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value));
      // Marked so a hot reload in development cannot leave duplicates behind.
      el.dataset.instructorPwa = 'true';
      document.head.appendChild(el);
      return el;
    });

    // The admin portal's viewport allows pinch-zoom, which is right there and
    // wrong on a register: a stray two-finger touch while marking a class
    // leaves the roster zoomed and an instructor stranded mid-attendance.
    const viewport = document.querySelector('meta[name="viewport"]');
    const previousViewport = viewport?.getAttribute('content');
    viewport?.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, viewport-fit=cover'
    );

    document.body.classList.add('instructor-surface');

    return () => {
      added.forEach((el) => el.remove());
      if (viewport && previousViewport) viewport.setAttribute('content', previousViewport);
      document.body.classList.remove('instructor-surface');
    };
  }, []);
}

// The wall-mounted iPad.
//
// No login, deliberately: the client rejected passwords and even a PIN for
// instructors, because they do the bare minimum and any friction means
// attendance simply stops being taken. The device itself is the credential —
// registered once by an admin, held in this browser from then on, and useless
// on any other device.

export default function InstructorPortal() {
  useHomeScreenApp();

  // Always ask the server, even with nothing in storage.
  //
  // This used to be answered from localStorage alone, which was quicker and
  // wrong: the device's token is also held in a cookie this page cannot read
  // (httpOnly, deliberately), so an iPad whose localStorage iOS has cleared —
  // or one opened from the home-screen icon, which gets its own storage
  // container — is still a registered device and must not be shown a pairing
  // screen. The boot check is one request and settles it properly.
  const [state, setState] = useState('checking');
  const [instructors, setInstructors] = useState([]);
  const [instructor, setInstructor] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [openClassId, setOpenClassId] = useState(null);
  const [pairing, setPairing] = useState(false);
  const [error, setError] = useState('');

  // Everything they teach that today's list isn't already showing.
  const otherClasses = useMemo(() => {
    if (!schedule) return [];
    const todayIds = new Set(schedule.today.map((cls) => cls.id));
    return schedule.allClasses.filter((cls) => !todayIds.has(cls.id));
  }, [schedule]);

  // Verifies the device is still registered and returns the instructor list.
  // Fetching only — the callers decide what to do with the answer.
  const loadInstructors = useCallback(async () => {
    await getInstructorSession();
    const { data } = await getPortalInstructors();
    return data.instructors;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const list = await loadInstructors();
        if (!cancelled) { setInstructors(list); setState('ready'); }
      } catch {
        if (!cancelled) setState('unregistered');
      }
    };

    run();
    return () => { cancelled = true; };
  }, [loadInstructors]);

  const chooseInstructor = async (name) => {
    setInstructor(name);
    setSchedule(null);
    setError('');
    try {
      const { data } = await getInstructorSchedule(name);
      setSchedule(data);
      // Straight into the class that is on — the whole point of the screen is
      // that an instructor walking up mid-class sees the register, not a menu.
      if (data.current) setOpenClassId(data.current.id);
    } catch (scheduleError) {
      setError(scheduleError.response?.data?.error || 'Could not load their classes');
    }
  };

  // Swap the six-character code for this device's own long-lived token.
  const pair = async (code) => {
    setPairing(true);
    setError('');
    try {
      const { data } = await pairDevice(code);
      setDeviceToken(data.token);
      setInstructors(await loadInstructors());
      setState('ready');
    } catch (pairError) {
      // Never leave a half-set credential behind on a failed attempt.
      clearDeviceToken();
      setError(pairError.response?.data?.error || 'Could not connect. Check the code and try again.');
    } finally {
      setPairing(false);
    }
  };

  if (state === 'checking') {
    return (
      <Shell>
        <div className="text-center">
          <div className="mb-6 flex justify-center"><Mark size="lg" /></div>
          <p className="text-xl text-[#f7f1e6]/70">Starting up…</p>
        </div>
      </Shell>
    );
  }

  if (state === 'unregistered') {
    return (
      <Shell>
        <div className="w-full max-w-lg text-center">
          <div className="mb-6 flex justify-center"><Mark size="lg" /></div>
          <h1 className="mb-2 text-3xl font-bold text-[#f7f1e6]">Set up this iPad</h1>
          <p className="mb-8 text-[#f7f1e6]/60">
            In the portal, open Admin Settings and register this device. Type the
            six characters it gives you below — once only. Nobody has to log in here
            again afterwards.
          </p>

          <PairingCodeInput
            onComplete={pair}
            disabled={pairing}
            error={error}
          />

          {pairing && <p className="mt-4 text-[#f7f1e6]/60">Connecting…</p>}
        </div>
      </Shell>
    );
  }

  if (openClassId) {
    return (
      <div className="h-screen overflow-hidden">
        <ClassRegister
          classId={openClassId}
          instructor={instructor}
          onBack={() => setOpenClassId(null)}
        />
      </div>
    );
  }

  // An instructor is chosen but no class is open — either nothing is on right
  // now, or they backed out of the register.
  if (instructor && schedule) {
    return (
      <Shell align="start">
        <div className="w-full max-w-4xl">
          <div className="mb-6 flex items-center gap-4">
            <Mark />
            <button
              onClick={() => { setInstructor(null); setSchedule(null); }}
              className="rounded-xl border border-[#443040] px-5 py-3 text-[#f7f1e6]/70"
            >
              ← Someone else
            </button>
          </div>

          <h1 className="mb-1 text-3xl font-bold text-[#f7f1e6]">{instructor}</h1>

          {/* Today first, because that is what an instructor standing here
              wants nine times out of ten. */}
          {schedule.today.length > 0 && (
            <>
              <p className="mb-4 text-[#f7f1e6]/60">Your classes today</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {schedule.today.map((cls) => (
                  <ClassCard key={cls.id} cls={cls} onOpen={setOpenClassId} />
                ))}
              </div>
            </>
          )}

          {/* And then everything else they teach, always — not only on a day
              with nothing scheduled.

              Competition teams are the reason. Their sessions move week to
              week, so the day stored on the class is a rough guide rather than
              a fact, and an instructor who is already teaching something today
              still needs to reach the team they are seeing tonight. Hiding the
              rest of their classes on a busy day left deleting and recreating
              the class as the only way through, which costs the roster and the
              class's whole attendance history. The register itself never cared:
              it records against the real calendar date whatever day the class
              says it runs. */}
          {otherClasses.length > 0 && (
            <>
              <p className={`mb-4 text-[#f7f1e6]/60 ${schedule.today.length > 0 ? 'mt-9' : ''}`}>
                {schedule.today.length > 0
                  ? 'Your other classes — open any of them to take a register for today'
                  : 'Nothing scheduled today — open any class to take a register for today'}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {otherClasses.map((cls) => (
                  <ClassCard key={cls.id} cls={cls} onOpen={setOpenClassId} />
                ))}
              </div>
            </>
          )}

          {error && <p className="mt-4 text-[#f58baa]">{error}</p>}
        </div>
      </Shell>
    );
  }

  return (
    <Shell align="start">
      <div className="w-full max-w-5xl">
        <div className="mb-6 flex items-center gap-4">
          <Mark />
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#f7f1e6]/45">
            The Stable
          </span>
        </div>

        <h1 className="mb-2 text-4xl font-bold text-[#f7f1e6]">Who's teaching?</h1>
        <p className="mb-8 text-lg text-[#f7f1e6]/55">
          Tap your card — attendance for your current class opens straight away.
        </p>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {instructors.map((person) => (
            <button
              key={person.name}
              onClick={() => chooseInstructor(person.name)}
              className={`rounded-3xl border-2 p-6 text-center transition-all ${
                person.teachingNow
                  ? 'border-[#ee8a1e] bg-[#382334]'
                  : 'border-[#443040] bg-[#2e1c2a] hover:border-[#ee8a1e]'
              }`}
            >
              <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-[#443040] text-3xl font-bold text-[#f7f1e6]">
                {person.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
              </div>
              <div className="text-2xl font-bold text-[#f7f1e6]">{person.name}</div>
              <div className={`mt-1 text-sm ${person.teachingNow ? 'text-[#ee8a1e]' : 'text-[#f7f1e6]/45'}`}>
                {person.status}
              </div>
            </button>
          ))}
        </div>

        <p className="mt-10 text-[#f7f1e6]/35">
          Covering someone's class? Just tap their card — no password needed.
        </p>
      </div>
    </Shell>
  );
}

// The Stable's mark, the same artwork the parent app launches with. An iPad on
// the academy wall is the most-seen screen in the building, so it should say
// whose it is without anyone reading a word.
/**
 * One class, as a tap target sized for someone at arm's length from a
 * wall-mounted iPad.
 */
function ClassCard({ cls, onOpen }) {
  return (
    <button
      onClick={() => onOpen(cls.id)}
      className={`rounded-2xl border p-5 text-left transition-colors ${
        cls.state === 'now'
          ? 'border-[#ee8a1e] bg-[#382334]'
          : 'border-[#443040] bg-[#2e1c2a] hover:bg-[#382334]'
      }`}
    >
      <div className="text-xl font-bold text-[#f7f1e6]">{cls.name}</div>
      <div className="text-[#f7f1e6]/60">
        {cls.day} {cls.startTimeLabel} · {cls.studentCount} students
      </div>
      <div className="mt-2 text-sm">
        {cls.state === 'now' && <span className="font-semibold text-[#ee8a1e]">On now</span>}
        {cls.markedCount > 0 && (
          <span className="text-[#7fc99a]">
            {cls.state === 'now' ? ' · ' : ''}{cls.markedCount} marked
          </span>
        )}
      </div>
    </button>
  );
}

function Mark({ size = 'md' }) {
  const box = size === 'lg' ? 'h-24 w-24 rounded-3xl' : 'h-11 w-11 rounded-xl';
  return (
    <img
      src="/brand/stable-mark-512.png"
      alt="The Stable"
      className={`${box} shrink-0 object-cover shadow-lg shadow-black/30`}
    />
  );
}

function Shell({ children, align = 'center' }) {
  return (
    <div
      className={`flex h-screen w-full overflow-y-auto bg-[#221420] p-8 ${
        align === 'center' ? 'items-center justify-center' : 'items-start justify-center pt-12'
      }`}
    >
      {children}
    </div>
  );
}
